import { Box, IconWithTextLockup, NavList } from '@deckee/deck-hand';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { useAppDispatch } from '../../../../services/store';
import {
  postMemberSettings,
  selectUnitsConfig,
} from '../../../../services/store/modules/app-controls';
import api from '../../../../services/store/modules/marketplace/api';
import { formatLatLon } from '../../../helpers/coordinate-helpers';
import BlueCircleWrapper from '../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import { ReactSvg, rules } from '../../../shared/vector-images';
import { useRadioRanges } from '../hooks/useRadioRanges';

export interface SafetyGearLocationCardInterface {
  latitude: number;
  longitude: number;
  showLatLon?: boolean;
  chevron?: boolean;
  blueVersion?: boolean;
  heading?: string;
  description?: string;
}

const SafetyGearLocationCard: FC<SafetyGearLocationCardInterface> = ({
  latitude,
  longitude,
  showLatLon,
  chevron,
  blueVersion,
  heading,
  description,
}) => {
  const dispatch = useAppDispatch();
  const [currentWaterBodyKey, setCurrentWaterBodyKey] = useState('');
  const [currentWaterBodyName, setCurrentWaterBodyName] = useState(description);
  const unitsConfig = useSelector(selectUnitsConfig);
  const { radioChannels, isEnabled, isLoading } = useRadioRanges({
    lat: latitude,
    lon: longitude,
  });

  const theme = useTheme();
  const history = useHistory();

  useEffect(() => {
    const updateCurrentWaterBody = async () => {
      if (latitude && longitude) {
        const { data } = await api.getWaterBodyDefinitionFromLocation({
          latitude,
          longitude,
        });

        setCurrentWaterBodyKey(data?.areaWaterBodyKey);
        setCurrentWaterBodyName(data?.name);
      }
    };
    updateCurrentWaterBody();
  }, [latitude, longitude]);

  const onClick = () => {
    captureEvent({
      type: 'prepare: go to prepare page',
      payload: {},
      sendToAmplitude: true,
    });

    if (!currentWaterBodyKey && !currentWaterBodyName) {
      history.push('/maps/prepare/checklist');
      return;
    }

    dispatch(
      postMemberSettings({
        waterBodyDefinitionConfig: {
          latitude,
          longitude,
          intendedWatersName: currentWaterBodyName || '',
          intendedAreaWaterBodyKey: currentWaterBodyKey,
        },
      }),
    );

    history.push('/maps/prepare/checklist');
  };

  const Wrapper = blueVersion ? BlueCircleWrapper : React.Fragment;

  return (
    <NavList.Item onClick={onClick} chevron={chevron}>
      <IconWithTextLockup
        image={
          <Wrapper>
            <ReactSvg
              toDisplay={rules()}
              options={{
                fillColor: blueVersion
                  ? theme.colors.secondary
                  : theme.colors.primary,
              }}
              width={24}
              height={24}
            />
          </Wrapper>
        }
        useEllipsis
        heading={heading}
        description={currentWaterBodyName}
      >
        {latitude && longitude && showLatLon && (
          <Box fontSize="1" color="darkergrey" mt="1">
            {formatLatLon(unitsConfig?.coordinate, latitude, longitude)}
          </Box>
        )}
      </IconWithTextLockup>
    </NavList.Item>
  );
};

SafetyGearLocationCard.defaultProps = {
  showLatLon: true,
  chevron: true,
  blueVersion: false,
  heading: 'Mandatory safety gear in this area',
  description: 'Move pin onto water',
};

export default SafetyGearLocationCard;
